<template>
  <div>
    <div class="ibox">
      <div class="ibox-title">
        <h2>Truck Description</h2>
      </div>
      <div class="ibox-content ibox-content-padding-xs">
        <form data-vv-scope="truck">
          <div class="row">
            <div class="col-md-6">
              <h3 class="m-b-lg">Details</h3>

              <div v-if="$user.isAdminOrCoordinator" class="form-group-outer">
                <div class="form-group float-label-control" :class="{ 'has-error': errors.has('truck.user_id') }">
                  <label for="" class="select-label">User</label>
                  <selectize
                    v-model="user"
                    name="user_id"
                    :options="initialUserOptions"
                    :fetch-results="fetchUserResults"
                    v-validate:user="{ required: $user.isAdminOrCoordinator }">
                  </selectize>
                </div>
              </div>

              <div class="form-group-outer">
                <div class="form-group float-label-control" :class="{ 'has-error': errors.has('truck.registration') }">
                  <label for="">Truck Registration (required)</label>
                  <input type="text" v-model="registration" :disabled="truckNotEditable" v-validate="'required'" name="registration" class="form-control" placeholder="Truck Registration (required)" v-float-label />
                </div>
              </div>

              <div class="form-group-outer">
                <div class="form-group float-label-control">
                  <label for="">Truck Make</label>
                  <input type="text" v-model="make" :disabled="truckNotEditable" class="form-control" placeholder="Truck Make" v-float-label/>
                </div>
              </div>

              <div class="form-group-outer">
                <div class="form-group float-label-control">
                  <label for="">Truck Model</label>
                  <input type="text" v-model="model" :disabled="truckNotEditable" class="form-control" placeholder="Truck Model" v-float-label/>
                </div>
              </div>

              <div class="form-group-outer">
                <div class="form-group float-label-control">
                  <label for="">Make Year</label>
                  <input type="number" v-model="year" :disabled="truckNotEditable" class="form-control" placeholder="Make Year" v-float-label/>
                </div>
              </div>

              <div class="form-group-outer">
                <div class="form-group float-label-control form-group">
                  <label for="" class="select-label">Base Location</label>
                  <selectize
                    v-model="location"
                    name="location_id"
                    :disabled="truckNotEditable"
                    :options="initialLocationOptions"
                    :fetch-results="fetchLocationResults">
                  </selectize>
                </div>
              </div>
            </div>
            <div class="col-md-6">
              <h3 class="m-b-lg">Prime Mover Photo</h3>

              <photo-cropper
                name="truck"
                v-model="photo"
                :photo-storage-path="truck.photo_storage_path"
                :original-name="truck.photo_name"
                :original-data-uri="originalPhotoDataUri"
                class-name="truck-photo"
                label-text="File">
              </photo-cropper>
            </div>
          </div>
        </form>
      </div>
    </div>

    <div class="ibox ibox-primary-truck">
      <div class="ibox-title">
        <h2>Primary Driver</h2>
      </div>
      <div class="ibox-content ibox-content-padding-xs">
        <div class="row">
          <div class="col-sm-4 col-md-3">
            <img v-if="driverDecorated.thumbnail_url" class="truck-profile" :src="driverDecorated.thumbnail_url" alt="Driver Name">
          </div>
          <div class="col-sm-8 col-md-9">
            <div class="row">
              <div class="col-md-8">
                <div class="form-group-outer no-margin">
                  <div class="form-group float-label-control form-group">
                    <label for="" class="select-label">Select Driver</label>
                    <selectize
                      v-model="driver"
                      name="driver_id"
                      :disabled="truckNotEditable"
                      :options="initialDriverOptions"
                      :fetch-results="fetchDriverResults">
                    </selectize>
                  </div>
                  <p v-if="driver">
                    <template v-if="truck.driver.email">
                      <i class="fa fa-at"></i> <a href="mailto:${truck.driver.email}">{{ truck.driver.email }}</a><br />
                    </template>
                    <template v-if="truck.driver.mobile">
                      <i class="fa fa-phone"></i> {{ truck.driver.mobile }}
                    </template>

                    <ladda-button
                      :on-click="handleRemoveDriver"
                      :disabled="truckNotEditable"
                      type="danger"
                      size="small"
                      class="m-t-lg"
                      :max-width="true">
                      <i class="fa fa-times"></i> Remove Driver
                    </ladda-button>
                  </p>
                  <p v-else>
                    &nbsp;
                  </p>
                </div>
              </div>
              <div class="col-md-4">
                <!-- <button href="#" :disabled="truckNotEditable" class="btn btn-primary btn-sm" data-toggle="modal" data-target=".modal-driver-add"><i class="fa fa-plus"></i> Add Driver</button> -->
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="ibox">
      <div class="ibox-title">
        <h2>Configuration Options</h2>
      </div>
      <div class="ibox-content">

        <p>Please add the configuration options for this truck.</p>

        <ul class="list-detail list-detail-configuration">
          <li v-for="(config, index) in truck.configurations" :key="config.id">
            <div class="row">
              <div class="config-size col-md-2 col-lg-1">{{ config.net_weight }} t</div>
              <div class="config-description col-md-10 col-lg-11">{{ config.description }}</div>
            </div>
            <a @click="handleDeleteConfig(index)" v-show="truckIsEditable && config.deleteable"><i class="fa fa-trash-alt"></i></a>
          </li>
        </ul>
      </div>

      <div class="ibox-footer ibox-footer-configuration-options">
        <p class="m-b-md"><strong>Add Configuration:</strong></p>
          <form data-vv-scope="configuration">
            <div class="row">
              <div class="col-md-4 col-lg-4">
                <div class="form-group-outer">
                  <div class="form-group float-label-control" :class="{'has-error' : errors.has('configuration.net_weight')}">
                    <label for="" class="select-label">Net Weight</label>
                    <div class="input-group">
                      <input type="number" :disabled="truckNotEditable" min="0" v-model="configuration.net_weight" name="net_weight" class="form-control"
                      placeholder="Net Weight" v-validate="'required'"/>
                      <span class="input-group-addon">t</span>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-md-8 col-lg-6">
                <div class="form-group-outer form-group-outer-description">
                  <div class="form-group float-label-control" :class="{'has-error' : errors.has('configuration.configuration_id')}">
                    <label for="" class="select-label">Description</label>
                    <selectize
                    v-model="configuration.id"
                    name="configuration_id"
                    :disabled="truckNotEditable"
                    v-validate="'required'"
                    :options="configurationOptions"
                    class="form-control select2-material-placeholder">
                  </selectize>
                </div>
              </div>
            </div>
            <div class="col-md-3 col-lg-2">
              <ladda-button :on-click="handleAddConfiguration" :disabled="truckNotEditable" size="small" class="m-t-sm" :max-width="true"><i class="fa fa-plus"></i> Add</ladda-button>
            </div>
          </div>
        </form>

      </div>

    </div>
    <driver-modal v-model="driver" :truck-user="user"></driver-modal>

    <ladda-button type="success" :disabled="truckNotEditable" :on-click="handleSaveChange"> Save Changes</ladda-button>
    <div class="margin-20"></div>
  </div>
</template>

<script>
  import { mapMutations, mapState, mapActions, mapGetters } from 'vuex'
  import { LaddaButton, DateRange, Selectize, PhotoCropper } from 'components/forms'
  import { RESET_TRUCK, SET_TRUCK, SET_TRUCK_ATTRIBUTE, ADD_LOCATION, ADD_USERS, PUSH_DRIVER, ADD_TRUCK_CONFIGURATION, REMOVE_TRUCK_CONFIGURATION } from 'vx/mutations'
  import { notify } from 'utils'
  import { Roles } from 'utils/user'
  import { stateMapper } from 'mixins/trucks'
  import { find, clone } from 'lodash'
  import DriverModal from './DriverModal'
  import $ from 'jquery'

  const defaultTruckConfiguration = {
    net_weight: null,
    id: null,
    description: null,
    deleteable: true
  }

  export default {
    mixins: [stateMapper],

    components: {
      LaddaButton,
      DateRange,
      Selectize,
      DriverModal,
      PhotoCropper
    },

    data () {
      return {
        dirtyPropertyWatching: 'truck',
        initialUserOptions: [],
        initialLocationOptions: [],
        initialDriverOptions: [],
        configuration: clone(defaultTruckConfiguration),
        originalPhotoDataUri: null,
        photo: null
      }
    },

    /**
     * On created reset the truck, if admin we fetch the users, and then we attempt
     * to load an existing truck if the route deems it necessary.
     *
     * @return void
     */
    created () {
      this[RESET_TRUCK]()

      this.getConfigurations()

      this.attemptToLoadExistingTruck().then(() => {
        // For admins we need to fetch all users as admins can modify everyones trucks.
        if (this.$user.isAdminOrCoordinator) {
          this.getUsers({ master: true, roles: this.userRoles }).then(this.setInitialUserOptions)

          // The user is either the current selected user of the truck OR we'll default
          // to the logged in users ID.
          this.user = this.truck.user_id || this.$user.id
        }

        // we need to load the public locations when creating a new truck
        if (!this.user) {
          this.getLocations().then(this.setInitialLocationOptions)
          this.getDrivers().then(this.setInitialDriverOptions)
        }
      })
    },

    mounted () {
      $('.modal-driver-add').on('hide.bs.modal', this.setInitialDriverOptions)
    },

    beforeDestroy () {
      $('.modal-driver-add').off('hide.bs.modal', this.setInitialDriverOptions)
    },

    watch: {
      /**
       * When the route changes we need to reset the truck because this form is used
       * to create and edit trucks. We will also attempt to reload an existing
       * truck from the database.
       */
      '$route' () {
        this[RESET_TRUCK]()

        this.attemptToLoadExistingTruck()
      },

      /**
       * Watch the user and get drivers/locations for selected user.
       *
       * @return void
       */
      user () {
        if (this.user) {
          this.getDrivers({ user_id: this.user }).then(this.setInitialDriverOptions)
        }

        this.getLocations({ user_id: this.user, public: true })
          .then(this.addLocationAndDriverToExisting)
          .then(this.setInitialLocationOptions)
          .then(this.setInitialUserOptions)
      }
    },

    computed: {
      ...mapState('truck', ['truck']),
      ...mapState('location', ['locations']),
      ...mapState('driver', ['drivers']),
      ...mapState('truck/configuration', ['configurations']),
      ...mapGetters('user', ['userOptions']),
      ...mapGetters('location', ['locationOptions']),
      ...mapGetters('driver', ['driverOptions']),
      ...mapGetters('truck', ['truckPayload', 'driverDecorated']),
      ...mapGetters('truck/configuration', ['configurationOptions']),

      /**
       * Determine if the truck is not editable
       *
       * @return boolean
       */
      truckNotEditable () {
        return this.$user.isDisabled
      },

      /**
       * Determine if the truck is editable
       *
       * @return boolean
       */
      truckIsEditable () {
        return this.$user.isEnabled
      },

      /**
       * Determine if we are creating a new truck.
       *
       * @return boolean
       */
      creatingNewTruck () {
        return this.$route.name === 'trucks.create'
      },

      /**
       * Determine if we are editing an existing truck.
       *
       * @return boolean
       */
      editingExistingTruck () {
        return this.$route.name === 'trucks.edit'
      },

      /**
       * Determine what roles we are showing on the user selection list
       *
       * @return Array rules
       */
      userRoles () {
        let roles = [Roles.CARRIER]

        if (this.$user.isAdmin) {
          roles.concat([Roles.ADMIN, Roles.COORDINATOR])
        }

        return roles
      }
    },

    methods: {
      ...mapMutations('truck', [RESET_TRUCK, SET_TRUCK, SET_TRUCK_ATTRIBUTE, ADD_TRUCK_CONFIGURATION, REMOVE_TRUCK_CONFIGURATION]),
      ...mapMutations('location', [ADD_LOCATION]),
      ...mapMutations('user', [ADD_USERS]),
      ...mapMutations('driver', [PUSH_DRIVER]),
      ...mapActions('truck', [
        'storeTruck',
        'updateTruck',
        'deleteTruck',
        'getTruck',
        'uploadTruckPhoto',
        'getTruckPhotoDataUri'
      ]),
      ...mapActions('user', ['getUsers']),
      ...mapActions('location', ['getLocations', 'getLocation']),
      ...mapActions('driver', ['getDrivers', 'getDriver']),
      ...mapActions('truck/configuration', ['getConfigurations']),

      /**
       * Set the initial driver options after selecting a user.
       *
       * @return void
       */
      setInitialDriverOptions () {
        this.initialDriverOptions = clone(this.driverOptions)
      },
      /**
       * Set the initial location options after selecting a user.
       *
       * @return void
       */
      setInitialUserOptions () {
        this.initialUserOptions = clone(this.userOptions)
      },
      /**
       * Set the initial location options after selecting a user.
       *
       * @return void
       */
      setInitialLocationOptions () {
        this.initialLocationOptions = clone(this.locationOptions)
      },

      /**
       * Add truck location back to location options
       *
       * @param  response
       */
      addLocationAndDriverToExisting (response) {
        if (this.truck.location) {
          this[ADD_LOCATION](this.truck.location)
        }

        if (this.truck.driver) {
          this[PUSH_DRIVER](this.truck.driver)
        }

        if (this.truck.user) {
          this[ADD_USERS]([this.truck.user])
          this.user = this.truck.user.id
        }
        return response
      },

      /**
       * Attempt to load and edit an existing truck.
       *
       * @return void
       */
      attemptToLoadExistingTruck () {
        if (this.editingExistingTruck) {
          const truckNotFound = () => {
            notify.error('This truck could be not found.')

            this.$router.push({ name: 'trucks.index' })
          }

          const fetchOriginalPhotoDataUri = () => {
            return this.getTruckPhotoDataUri({ id: this.truck.id, dimension: 'original' })
                    .then(response => (this.originalPhotoDataUri = response.data.data_uri))
          }

          return this.getTruck(this.$route.params.id)
            .then(this.addLocationAndDriverToExisting)
            .then(fetchOriginalPhotoDataUri)
            .catch(truckNotFound)
        }

        return Promise.resolve()
      },

      handleRemoveDriver () {
        return this.handleSaveTruck({ ...this.truck, driver: null, driver_id: null })
          .then(() => {
            notify.success('Driver has been removed.')
          })
      },

      handleSaveChange () {
        return this.handleSaveTruck(this.truck)
          .then(() => {
            notify.success('Truck has been saved.')
          })
      },

      /**
       * Handle saving a new or existing truck.
       *
       * @return Promise
       */
      handleSaveTruck (props) {
        const reportMissingNameError = () => {
          if (this.errors.has('registration')) {
            notify.error('A registration number is required.')
          }
        }

        return this.validateScope('truck').then(() => {
          if (this.editingExistingTruck) {
            let payload = {
              id: this.truck.id,
              payload: props
            }

            return this.updateTruck(payload)
              .then(this.handlePhotoUpload)
              .then(this.updateTruckState)
          } else {
            return this.storeTruck(this.truckPayload)
              .then(this.handlePhotoUpload)
              .then(this.redirectToEditTruck)
          }
        }).catch(reportMissingNameError)
      },

      /**
       * Handle uploading truck photo.
       *
       * @param object response
       *
       * @return Promise
       */
      handlePhotoUpload (response) {
        if (this.photo) {
          return this.uploadTruckPhoto({ id: response.data.id, file: this.photo.file, crop: this.photo.crop })
        }

        return Promise.resolve(response)
      },

      /**
       * Delete trucks configuration option
       */
      handleDeleteConfig (configurationIndex) {
        this[REMOVE_TRUCK_CONFIGURATION](configurationIndex)
      },

      /**
       * Add the inputted configuration to the the trucks configurations
       * They will be saved when the Save button is clicked
       */
      handleAddConfiguration () {
        return this.validateScope('configuration').then(() => {
          this.configuration.description = find(this.configurationOptions, option => option.id === this.configuration.id).text

          this[ADD_TRUCK_CONFIGURATION](this.configuration)

          this.configuration = clone(defaultTruckConfiguration)

          this.$nextTick(this.clearValidationErrors)

          return Promise.resolve()
        })
      },

      /**
       * When user types in the location select box we need to fetch more locations
       * from the api since the locations are paginated
       */
      fetchLocationResults (keywords, callback) {
        return this.getLocations({ keywords, public: true, user_id: this.user }).then(response => {
          callback(this.locationOptions)
        })
      },

      /**
       * When user types in the driver select box we need to fetch more drivers
       * from the api since the drivers are paginated
       */
      fetchDriverResults (name, callback) {
        return this.getDrivers({ user_id: this.user, name }).then(response => {
          callback(this.driverOptions)
        })
      },

      /**
       * When admin types in the user select box we need to fetch more users
       * from the api since the users are paginated
       */
      fetchUserResults (name, callback) {
        return this.getUsers({ master: true, roles: [Roles.ADMIN, Roles.CARRIER, Roles.COORDINATOR], name }).then(response => {
          callback(this.userOptions)
        })
      },

      /**
       * Update the truck state with the response data.
       *
       * @param object response
       *
       * @return void
       */
      updateTruckState (response) {
        this[SET_TRUCK](response.data)
      },

      /**
       * Redirect to the edit location after creating/editing a location.
       *
       * @param object response
       *
       * @return void
       */
      redirectToEditTruck (response) {
        this.$router.push({ name: 'trucks.edit', params: { id: response.data.id } })
      }
    }
  }
</script>



// WEBPACK FOOTER //
// Form.vue?05cc15ee